import React, { useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const STAGES = [
  { key: 'prospect',    label: 'Prospect',    color: 'bg-gray-100 border-gray-300 text-gray-800' },
  { key: 'cultivating', label: 'Cultivating', color: 'bg-blue-50 border-blue-300 text-blue-800' },
  { key: 'solicited',   label: 'Solicited',   color: 'bg-purple-50 border-purple-300 text-purple-800' },
  { key: 'donor',       label: 'Donor',       color: 'bg-emerald-50 border-emerald-300 text-emerald-800' },
  { key: 'steward',     label: 'Steward',     color: 'bg-teal-50 border-teal-300 text-teal-800' },
  { key: 'lapsed',      label: 'Lapsed',      color: 'bg-red-50 border-red-300 text-red-800' },
];

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * Pipeline Kanban — drag-and-drop between 6 stages. Optimistic UI, calls
 * PUT /donors/{id} with pipeline_stage on drop. Reverts if the API fails.
 */
export function PipelineBoard({ donors, onChange, api }) {
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const byStage = useCallback((stage) => donors.filter(d => (d.pipeline_stage || 'prospect') === stage), [donors]);

  const onDrop = async (stage) => {
    setDropTarget(null);
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const donor = donors.find(d => d.id === id);
    if (!donor || (donor.pipeline_stage || 'prospect') === stage) return;
    setSaving(true);
    try {
      await api.put(`/donors/${id}`, { pipeline_stage: stage });
      toast.success(`${donor.name} → ${STAGES.find(s => s.key === stage)?.label}`);
      onChange();
    } catch {
      toast.error('Failed to move donor');
    }
    setSaving(false);
  };

  return (
    <div className="relative" data-testid="pipeline-board">
      {saving && <div className="absolute top-1 right-1 z-10"><Loader2 className="h-4 w-4 animate-spin text-[#6B3FA0]" /></div>}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {STAGES.map(stage => {
          const list = byStage(stage.key);
          const total = list.reduce((s, d) => s + (d.total_given || 0), 0);
          const isTarget = dropTarget === stage.key;
          return (
            <div key={stage.key}
                 data-testid={`stage-col-${stage.key}`}
                 onDragOver={(e) => { e.preventDefault(); setDropTarget(stage.key); }}
                 onDragLeave={() => setDropTarget(t => t === stage.key ? null : t)}
                 onDrop={() => onDrop(stage.key)}
                 className={`rounded-lg border-2 transition-colors ${isTarget ? 'border-[#6B3FA0] bg-purple-50' : 'border-transparent'}`}>
              <div className={`px-2.5 py-1.5 rounded-t border-t-4 ${stage.color}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">{stage.label}</span>
                  <Badge variant="outline" className="text-xs">{list.length}</Badge>
                </div>
                <div className="text-[10px] text-gray-600">{fmt$(total)} lifetime</div>
              </div>
              <div className="p-1.5 space-y-1.5 min-h-[280px]">
                {list.length === 0 && <div className="text-center text-xs text-gray-400 pt-8">Drop donors here</div>}
                {list.map(d => (
                  <Card key={d.id}
                        draggable
                        onDragStart={() => setDragId(d.id)}
                        onDragEnd={() => setDragId(null)}
                        onClick={() => navigate(`/donors/${d.id}`)}
                        className={`cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${dragId === d.id ? 'opacity-40' : ''}`}
                        data-testid={`stage-card-${d.id}`}>
                    <CardContent className="p-2">
                      <div className="text-sm font-medium truncate">{d.name}</div>
                      {d.org && <div className="text-[11px] text-gray-500 truncate">{d.org}</div>}
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[11px] text-gray-500">{d.gift_count || 0} gifts</span>
                        <span className="text-xs font-bold tabular-nums">{fmt$(d.total_given)}</span>
                      </div>
                      {(d.tags || []).slice(0, 2).map(t => (
                        <span key={t} className="inline-block text-[9px] bg-gray-100 rounded px-1 py-0.5 mr-1 mt-1">{t}</span>
                      ))}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
